import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { GlobalContext } from 'providers/GlobalProvider';
import {
  IconZap,
  IconDatabase,
  IconTarget,
  IconArrowRight,
  IconShieldCheck,
  IconX,
  IconCreditCard,
} from './SVGIcons';
import CreditsAPI from '../../api/CreditsAPI';
import {
  useGetProductDbStatsByGroupId,
  useGetStatsByGroupId,
} from '../../api/bitedb/stats/stats.hooks';
import { ExportHubAPI } from '../../api/ExportHubAPI';

type PoolSummary = {
  total: number;
  used: number;
  remaining: number;
  dbSize: number;
  productDbSize: number;
};

type SinglePoolSummary = {
  total: number;
  used: number;
};

type UsageBreakdown = {
  paid: {
    total_monthly_credits: number;
    total_used_credits: number;
    remaining_credits: number;
    db_size: number;
    product_db_size: number;
    // Workflows' own pool -- see team_credits.workflow_credits/used_workflow_credits.
    total_workflow_credits: number;
    total_used_workflow_credits: number;
    remaining_workflow_credits: number;
    // Slice of the credits + workflow pools on the non-expiring one-time row.
    one_time_credits: number;
    one_time_used_credits: number;
    one_time_workflow_credits: number;
    one_time_used_workflow_credits: number;
  };
  trial: {
    credits_total: number;
    credits_used: number;
    remaining_credits: number;
    db_size: number;
    product_db_size: number;
  };
  analyzerSpendBySource: Record<string, number>;
};

type CreditTransaction = {
  event_type: 'grant' | 'spend';
  amount: number;
  source: string;
  credit_type:
    | 'analyzer'
    | 'entity_db'
    | 'product_db'
    | 'credits'
    | 'tool'
    | 'workflow'
    | 'verification';
  created_at: string;
  // Set by the server on credit grants: which pool the grant went to.
  credit_frequency?: 'recurring' | 'one_time' | null;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  // Workflow spends only: the run this spend belongs to and the title of the step it paid for.
  run_id?: string | null;
  workflow_step?: string | null;
  // Signed delta: positive for a grant, negative for a spend (mirrors
  // CreditTransactionLog.credit_delta; computed the same way for the
  // synthetic grouped Entity/Import/Export Hub rows, which have no such
  // column of their own to select from).
  credit_delta: number;
};

// credit_type -> the 3 real credit packs (Analyzers, Database,
// Workflows). entity_db/product_db both fold into "Database" (still two
// server-side ceilings, one pack); 'credits' is the legacy generic tag for
// team_credits.monthly_credits, which is Analyzers' pool.
// Names match the Remaining Balance rows (Credits / Database Records / ...).
const CREDIT_TYPE_LABELS: Record<CreditTransaction['credit_type'], string> = {
  analyzer: 'Credits',
  credits: 'Credits',
  tool: 'Tool',
  workflow: 'Credits',
  entity_db: 'Database Records',
  product_db: 'Database Records',
  verification: 'Business Verification',
};

const SOURCE_LABELS: Record<string, string> = {
  ace_analyzer: 'ACE Analyzer',
  aes_analyzer: 'AES Analyzer',
  tariff_analyzer: 'Tariff Analyzer',
  import_workflow: 'Import Workflow',
  export_workflow: 'Export Workflow',
  design_assistant: 'Design Assistant',
  beneficial_ownership: 'Beneficial Ownership',
  entity_risk: 'Export and Transshipment Risk',
  business_verification: 'Business Verification',
  entity_hub: 'Entity Hub',
  import_hub: 'Import Hub',
  export_hub: 'Export Hub',
  manual_admin: 'Manual adjustment',
  stripe_purchase: 'Purchase',
  entity_screening_tool: 'Entity Screening',
  classification_tool: 'Classification Tool',
  ECCN_check_tool: 'ECCN Check Tool',
  Import_screening_tool: 'Import Screen Tool',
  Export_screening_tool: 'Export Screen Tool',
};

// Credit grants are split the same way as the Remaining Balance section
// (the server classifies each grant). Spends can't be attributed to a pool.
const creditTypeLabel = (tx: CreditTransaction): string => {
  const base = CREDIT_TYPE_LABELS[tx.credit_type] ?? tx.credit_type;
  if (tx.event_type !== 'grant' || base !== 'Credits') return base;
  if (tx.credit_frequency === 'one_time') return 'One-Time Credits';
  if (tx.credit_frequency === 'recurring') return 'Credits Auto-Refresh';
  return base;
};

const toPoolSummary = (paid: UsageBreakdown['paid']): PoolSummary => ({
  total: Number(paid?.total_monthly_credits ?? 0),
  used: Number(paid?.total_used_credits ?? 0),
  remaining: Number(paid?.remaining_credits ?? 0),
  dbSize: Number(paid?.db_size ?? 0),
  productDbSize: Number(paid?.product_db_size ?? 0),
});

const toWorkflowPoolSummary = (
  paid: UsageBreakdown['paid'],
): SinglePoolSummary => ({
  total: Number(paid?.total_workflow_credits ?? 0),
  used: Number(paid?.total_used_workflow_credits ?? 0),
});

const toTrialPoolSummary = (trial: UsageBreakdown['trial']): PoolSummary => ({
  total: Number(trial?.credits_total ?? 0),
  used: Number(trial?.credits_used ?? 0),
  remaining: Number(trial?.remaining_credits ?? 0),
  dbSize: Number(trial?.db_size ?? 0),
  productDbSize: Number(trial?.product_db_size ?? 0),
});

const pct = (used: number, total: number): number =>
  total > 0 ? Math.min((used / total) * 100, 100) : 0;

type UsageRangeKey = '1d' | '3d' | '7d' | '30d' | '3m';

// days is what the server is asked for (it caps at 90); unit is the bucket
// size the graph plots. Label rules per range live in buildUsageBuckets.
const USAGE_RANGES: {
  key: UsageRangeKey;
  label: string;
  days: number;
  unit: 'hour' | 'day';
}[] = [
  { key: '1d', label: '1 Day', days: 1, unit: 'hour' },
  { key: '3d', label: '3 Days', days: 3, unit: 'day' },
  { key: '7d', label: '7 Days', days: 7, unit: 'day' },
  { key: '30d', label: '30 Days', days: 30, unit: 'day' },
  { key: '3m', label: '3 Months', days: 90, unit: 'day' },
];

// 3 Months labels the x axis with the week-ending date; weeks end Saturday
// (moment's default en locale), so only Saturdays get a tick label.
const WEEK_ENDING_DAY = 6;

type UsageBucket = {
  key: string;
  tooltipLabel: string;
  axisLabel: string;
  total: number;
};

// One row of the server-side aggregate (CreditsAPI.getUsageSeries): total
// credits for one hour/day bucket, keyed in the browser's own time zone.
type UsageSeriesRow = {
  bucket: string;
  event_type: 'grant' | 'spend';
  total: number;
};

const buildUsageBuckets = (
  range: (typeof USAGE_RANGES)[number],
  spends: UsageSeriesRow[],
): UsageBucket[] => {
  const buckets: UsageBucket[] = [];
  const isHourly = range.unit === 'hour';
  const count = isHourly ? 24 : range.days;
  const keyFormat = isHourly ? 'YYYY-MM-DD HH' : 'YYYY-MM-DD';
  const now = moment();
  for (let i = count - 1; i >= 0; i--) {
    const m = now.clone().subtract(i, range.unit === 'hour' ? 'hours' : 'days');
    let axisLabel: string;
    if (isHourly) {
      axisLabel = m.format('h A');
    } else if (range.key === '30d') {
      // Every 3rd day on the axis, counting back from today so the latest
      // day is always labeled; every day still gets a data point.
      axisLabel = i % 3 === 0 ? m.format('MMM D') : '';
    } else if (range.key === '3m') {
      axisLabel = m.day() === WEEK_ENDING_DAY ? m.format('MMM D') : '';
    } else {
      axisLabel = m.format('MMM D');
    }
    buckets.push({
      key: m.format(keyFormat),
      tooltipLabel: isHourly ? m.format('MMM D, h A') : m.format('ddd, MMM D'),
      axisLabel: axisLabel,
      total: 0,
    });
  }
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  spends.forEach((row) => {
    const bucket = byKey.get(row.bucket);
    if (bucket) bucket.total += Number(row.total || 0);
  });
  return buckets;
};

const REFRESH_RETRY_DELAY_MS = 1500;
const REFRESH_MAX_RETRIES = 4;

export const CreditsUsageOverview: React.FC<{
  cur_user: any;
  refreshToken?: number;
  onManageCredits: () => void;
  canManageBilling?: boolean;
  // Super-admin view of another team (TeamSelectView's Usage tab): reads via
  // the bite-admin endpoints instead of the caller's own team-scoped ones.
  adminTeamId?: string;
}> = ({
  cur_user,
  refreshToken = 0,
  onManageCredits,
  canManageBilling,
  adminTeamId,
}) => {
  const [howOpen, setHowOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [breakdown, setBreakdown] = useState<UsageBreakdown | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [series, setSeries] = useState<UsageSeriesRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rangeKey, setRangeKey] = useState<UsageRangeKey>('7d');
  const range = USAGE_RANGES.find((r) => r.key === rangeKey) ?? USAGE_RANGES[3];
  const { theme } = useContext(GlobalContext);

  const isAdminView = Boolean(adminTeamId);
  const selectedTeamId = adminTeamId || cur_user?.selected_team?.id;

  // The Entity/Product/Export stats endpoints are team-member scoped, so the
  // admin view takes its row counts from an admin endpoint that runs the same queries.
  const { data: entityStatsData, refetch: refetchEntityStats } =
    useGetStatsByGroupId(isAdminView ? '' : selectedTeamId || '');
  const { data: productStatsData, refetch: refetchProductStats } =
    useGetProductDbStatsByGroupId(isAdminView ? '' : selectedTeamId || '');
  // Record counts are cached for minutes; after a package purchase/refresh the
  // pack totals reload, so reload the used counts with them.
  useEffect(() => {
    if (refreshToken > 0 && !isAdminView && selectedTeamId) {
      refetchEntityStats();
      refetchProductStats();
    }
  }, [refreshToken]);
  const [adminDbTotals, setAdminDbTotals] = useState({
    entity: 0,
    product: 0,
    exportProduct: 0,
  });
  // Add-on credit balances. A row only appears below when the team has that
  // add-on's credit package (a balance above 0); a team may have one, both or
  // neither.
  const [addonCredits, setAddonCredits] = useState<{
    verificationTotal: number;
    verificationUsed: number;
    ownershipTotal: number;
    ownershipUsed: number;
  } | null>(null);
  useEffect(() => {
    if (!selectedTeamId) {
      setAddonCredits(null);
      return undefined;
    }
    let active = true;
    CreditsAPI.getVerificationCreditsSummary(String(selectedTeamId)).then(
      (resp: any) => {
        if (!active) return;
        setAddonCredits(
          resp?.success
            ? {
                verificationTotal: Number(
                  resp.result?.verification_credits ?? 0,
                ),
                verificationUsed: Number(resp.result?.used_this_month ?? 0),
                ownershipTotal: Number(
                  resp.result?.corporate_ownership_credits ?? 0,
                ),
                ownershipUsed: Number(
                  resp.result?.corporate_ownership_used_this_month ?? 0,
                ),
              }
            : null,
        );
      },
    );
    return () => {
      active = false;
    };
  }, [selectedTeamId, refreshToken]);
  useEffect(() => {
    if (!adminTeamId) return undefined;
    let active = true;
    CreditsAPI.getAdminDatabaseUsage(adminTeamId).then((resp) => {
      if (!active) return;
      const t = resp?.success ? resp.result : null;
      setAdminDbTotals({
        entity: Number(t?.entity ?? 0),
        product: Number(t?.product ?? 0),
        exportProduct: Number(t?.exportProduct ?? 0),
      });
    });
    return () => {
      active = false;
    };
  }, [adminTeamId, refreshToken]);
  const entityStats = isAdminView
    ? { total_entities: adminDbTotals.entity }
    : entityStatsData;
  const productStats = isAdminView
    ? { total_products: adminDbTotals.product }
    : productStatsData;
  const [exportProductsOwn, setExportProducts] = useState(0);
  const exportProducts = isAdminView
    ? adminDbTotals.exportProduct
    : exportProductsOwn;
  useEffect(() => {
    if (!selectedTeamId || isAdminView) {
      setExportProducts(0);
      return undefined;
    }
    let active = true;
    ExportHubAPI.getOverviewKpis(selectedTeamId)
      .then((k) => {
        if (active) setExportProducts(Number(k?.total ?? 0));
      })
      .catch(() => {
        if (active) setExportProducts(0);
      });
    return () => {
      active = false;
    };
  }, [selectedTeamId, isAdminView, refreshToken]);

  useEffect(() => {
    let active = true;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    const load = async (attempt = 0) => {
      if (!selectedTeamId) {
        if (!active) return;
        setBreakdown(null);
        setHasError(false);
        setIsLoading(false);
        return;
      }

      if (attempt === 0) {
        setIsLoading(true);
      }

      const usageResp = isAdminView
        ? await CreditsAPI.getAdminUsageBreakdown(selectedTeamId)
        : await CreditsAPI.getUsageBreakdown(selectedTeamId);

      if (!active) return;

      if (usageResp?.success) {
        setBreakdown(usageResp.result);
        setHasError(false);
      } else {
        setHasError(true);
      }

      const shouldRetry =
        refreshToken > 0 &&
        !usageResp?.success &&
        attempt < REFRESH_MAX_RETRIES;
      if (shouldRetry) {
        retryTimeout = setTimeout(() => {
          load(attempt + 1);
        }, REFRESH_RETRY_DELAY_MS);
        return;
      }

      setIsLoading(false);
    };

    load();

    return () => {
      active = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [selectedTeamId, refreshToken, isAdminView]);

  // Transactions and the aggregate series follow the selected range on their
  // own, so switching the dropdown doesn't reload the balances above. The
  // series (chart + totals) is grouped in SQL over the whole window; the raw
  // rows only feed the "Usage details" table and are capped by the server.
  useEffect(() => {
    let active = true;
    if (!selectedTeamId) {
      setTransactions([]);
      setSeries([]);
      return undefined;
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    (isAdminView
      ? CreditsAPI.getAdminUsageFeed(selectedTeamId, range.days, 1000)
      : CreditsAPI.getTransactions(selectedTeamId, range.days, 1000)
    ).then((resp) => {
      if (!active) return;
      setTransactions(
        resp?.success && Array.isArray(resp.result) ? resp.result : [],
      );
    });
    (isAdminView
      ? CreditsAPI.getAdminUsageSeries(
          selectedTeamId,
          range.days,
          range.unit,
          tz,
        )
      : CreditsAPI.getUsageSeries(selectedTeamId, range.days, range.unit, tz)
    ).then((resp) => {
      if (!active) return;
      setSeries(resp?.success && Array.isArray(resp.result) ? resp.result : []);
    });
    return () => {
      active = false;
    };
  }, [selectedTeamId, range.days, range.unit, refreshToken, isAdminView]);

  const paidSummary = useMemo(
    () => toPoolSummary(breakdown?.paid ?? ({} as UsageBreakdown['paid'])),
    [breakdown],
  );
  const trialSummary = useMemo(
    () =>
      toTrialPoolSummary(breakdown?.trial ?? ({} as UsageBreakdown['trial'])),
    [breakdown],
  );
  // Workflows has its own real balance now (team_credits.workflow_credits) --
  // trial has no separate Workflows allocation, so a trial-only team just
  // shows 0/0 here.
  const workflowsSummary = useMemo(
    () =>
      toWorkflowPoolSummary(breakdown?.paid ?? ({} as UsageBreakdown['paid'])),
    [breakdown],
  );
  // Analyzers' pool -- the historical monthly_credits/used_credits balance
  // (matching the "Analyzer Credits" Stripe product's own name), with a
  // trial-pool fallback since that's the pool a trial team's free
  // allocation has always lived in.
  const hasPaidPool =
    paidSummary.total > 0 ||
    paidSummary.used > 0 ||
    paidSummary.dbSize > 0 ||
    paidSummary.productDbSize > 0;
  const analyzersActive = hasPaidPool ? paidSummary : trialSummary;

  const entityRowsUsed = Number(entityStats?.total_entities ?? 0);
  const productRowsUsed =
    Number(productStats?.total_products ?? 0) + exportProducts;
  // Combined "Database" balance — Entity DB and Product DB are still two separate
  // limits server-side, but the design merges them into one pack/balance, so add
  // both the used counts and the totals together for a single row.
  const databaseRowsUsed = entityRowsUsed + productRowsUsed;
  const databaseTotal = analyzersActive.dbSize + analyzersActive.productDbSize;
  const databasePct = pct(databaseRowsUsed, databaseTotal);

  // Spend for the selected range, aggregated server-side from the real
  // CreditTransactionLog feed (hourly or daily buckets).
  const spendSeries = useMemo(
    () => series.filter((r) => r.event_type === 'spend'),
    [series],
  );
  const totalSpend = useMemo(
    () => spendSeries.reduce((sum, r) => sum + Number(r.total || 0), 0),
    [spendSeries],
  );
  const usageBuckets = useMemo(
    () => buildUsageBuckets(range, spendSeries),
    [range, spendSeries],
  );

  const chartData = useMemo(
    () => ({
      labels: usageBuckets.map((b) => b.axisLabel),
      datasets: [
        {
          label: 'Credits spent',
          data: usageBuckets.map((b) => b.total),
          borderColor: '#4895ef',
          backgroundColor: 'rgba(72, 149, 239, 0.15)',
          fill: true,
          tension: 0.3,
          borderWidth: 2,
          pointRadius: usageBuckets.length > 31 ? 0 : 3,
          pointHoverRadius: 5,
        },
      ],
    }),
    [usageBuckets],
  );
  const chartOptions = useMemo(() => {
    const tickColor = theme === 'light' ? '#475569' : '#94a3b8';
    const gridColor =
      theme === 'light'
        ? 'rgba(15, 23, 42, 0.08)'
        : 'rgba(148, 163, 184, 0.15)';
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: any[]) =>
              usageBuckets[items[0]?.dataIndex]?.tooltipLabel ?? '',
            label: (item: any) => {
              const v = Number(item.parsed.y);
              return `${v.toLocaleString()} credit${v === 1 ? '' : 's'}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: tickColor,
            autoSkip: false,
            maxRotation: 0,
            font: { size: 10 },
            // Empty axisLabels are the intentionally-skipped ticks; 1 Day
            // is hourly, so thin it to every 3rd hour.
            callback: (_v: any, idx: number) =>
              range.key === '1d' && idx % 3 !== 0
                ? ''
                : usageBuckets[idx]?.axisLabel ?? '',
          },
        },
        y: {
          beginAtZero: true,
          grace: '10%',
          grid: { color: gridColor },
          ticks: {
            color: tickColor,
            font: { size: 10 },
            precision: 0,
            maxTicksLimit: 5,
          },
        },
      },
    };
  }, [usageBuckets, range.key, theme]);

  // Additions (purchases, admin grants) -- excluded from the spend total/chart
  // above (which is specifically "credits spent"), but still real activity a
  // team should be able to see, so it gets its own total.
  const totalGrants = useMemo(
    () =>
      series
        .filter((r) => r.event_type === 'grant')
        .reduce((sum, r) => sum + Number(r.total || 0), 0),
    [series],
  );

  // "Usage details" modal shows one combined, date/time-sorted table (both
  // spends and purchases together) rather than two separate lists.
  const allTransactionsSorted = useMemo(
    () =>
      [...transactions].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [transactions],
  );

  // Credits = the shared pool that agentic workflows and the Bulk Tariff
  // Analyzer both spend from (any legacy standalone workflow balance is added in).
  const creditsUsed = analyzersActive.used + workflowsSummary.used;
  const creditsTotal = analyzersActive.total + workflowsSummary.total;
  // One-time packs live on their own non-expiring row; everything else in the
  // pool is the recurring (auto-refresh) allocation.
  const oneTimeTotal =
    Number(breakdown?.paid?.one_time_credits ?? 0) +
    Number(breakdown?.paid?.one_time_workflow_credits ?? 0);
  const oneTimeUsed =
    Number(breakdown?.paid?.one_time_used_credits ?? 0) +
    Number(breakdown?.paid?.one_time_used_workflow_credits ?? 0);
  const autoRefreshTotal = Math.max(0, creditsTotal - oneTimeTotal);
  const autoRefreshUsed = Math.max(0, creditsUsed - oneTimeUsed);
  const creditSpendRows = Object.entries(breakdown?.analyzerSpendBySource ?? {})
    .map(([source, amount]) => ({
      label: SOURCE_LABELS[source] ?? source,
      value: Number(amount || 0),
      unit: 'credits spent',
    }))
    .filter((r) => r.value > 0)
    .sort((x, y) => y.value - x.value);
  const databaseHubRows = [
    { label: 'Entity Hub', value: entityRowsUsed, unit: 'entities' },
    {
      label: 'Import Hub',
      value: Number(productStats?.total_products ?? 0),
      unit: 'products',
    },
    { label: 'Export Hub', value: exportProducts, unit: 'products' },
  ];

  const balanceRows = [
    {
      key: 'oneTimeCredits',
      label: 'One-Time Credits',
      rate: 'Purchased packs · never expire',
      icon: <IconCreditCard />,
      used: oneTimeUsed,
      total: oneTimeTotal,
      pct: pct(oneTimeUsed, oneTimeTotal),
      detailTitle: '',
      detailEmpty: '',
      detail: [],
    },
    {
      key: 'credits',
      label: 'Credits Auto-Refresh',
      rate: 'Monthly · Workflows & Bulk Tariff Analyzer',
      icon: <IconZap />,
      used: autoRefreshUsed,
      total: autoRefreshTotal,
      pct: pct(autoRefreshUsed, autoRefreshTotal),
      detailTitle: 'Credits spent by feature',
      detailEmpty: 'No credits spent yet.',
      detail: creditSpendRows,
    },
    {
      key: 'database',
      label: 'Database Records',
      rate: '1 credit = 1 entity or SKU',
      icon: <IconDatabase />,
      used: databaseRowsUsed,
      total: databaseTotal,
      pct: databasePct,
      detailTitle: 'Records stored by hub',
      detailEmpty: 'No records stored yet.',
      detail: databaseHubRows,
    },
  ];

  if (addonCredits && addonCredits.verificationTotal > 0) {
    balanceRows.push({
      key: 'businessVerification',
      label: 'Business Verification',
      rate: '1 credit = 1 entity verification',
      icon: <IconShieldCheck />,
      used: addonCredits.verificationUsed,
      total: addonCredits.verificationTotal,
      pct: pct(addonCredits.verificationUsed, addonCredits.verificationTotal),
      detailTitle: '',
      detailEmpty: 'No entity verifications yet this month.',
      detail: [
        {
          label: 'Entity verifications',
          value: addonCredits.verificationUsed,
          unit: 'credits spent',
        },
      ],
    });
  }
  if (addonCredits && addonCredits.ownershipTotal > 0) {
    balanceRows.push({
      key: 'corporateOwnership',
      label: 'Corporate Ownership Checks',
      rate: '1 credit = 1 ownership check',
      icon: <IconTarget />,
      used: addonCredits.ownershipUsed,
      total: addonCredits.ownershipTotal,
      pct: pct(addonCredits.ownershipUsed, addonCredits.ownershipTotal),
      detailTitle: 'Ownership check credits this month',
      detailEmpty: 'No ownership checks run yet this month.',
      detail: [
        {
          label: 'Checks run',
          value: addonCredits.ownershipUsed,
          unit: 'credits spent',
        },
      ],
    });
  }

  // Teams with no recurring credits (e.g. one-time packs only) don't get an
  // empty Auto-Refresh row.
  const visibleBalanceRows =
    autoRefreshTotal > 0 || autoRefreshUsed > 0
      ? balanceRows
      : balanceRows.filter((r) => r.key !== 'credits');

  if (!selectedTeamId) {
    return null;
  }

  return (
    <>
      <div className='c-overview-grid'>
        {/* Credits balance card */}
        <div className='c-overview-card'>
          <div className='c-overview-card-header'>
            <div className='c-usage-icon-wrap'>
              <IconZap />
            </div>
            <div className='c-usage-info'>
              <h3 className='c-usage-title'>Remaining Balance</h3>
              <p className='c-usage-cycle'>
                Credits and Database Records
                {isLoading
                  ? ' — Loading...'
                  : hasError
                  ? ' — Unable to load credits'
                  : ''}
              </p>
            </div>
            <button
              className='pricing-btn pricing-btn-outline c-overview-card-action'
              onClick={() => setHowOpen(true)}
            >
              How Credits Work
            </button>
            {canManageBilling && (
              <>
                <button
                  type='button'
                  className='tm-btn tm-btn-primary tm-license-overview-buy-btn'
                  onClick={onManageCredits}
                >
                  Top Up
                </button>
                <button
                  className='msp-ent-cta'
                  style={{
                    backgroundColor: 'var(--plan-enterprise)',
                    width: 'fit-content',
                  }}
                  disabled={!canManageBilling}
                  onClick={() =>
                    (window.location.href = 'mailto:info@bitedata.io')
                  }
                >
                  Contact Sales <IconArrowRight />
                </button>
              </>
            )}
          </div>
          <div className='c-overview-balances'>
            {visibleBalanceRows.map((row) => {
              // One-Time Credits has no breakdown to show.
              const canExpand = row.key !== 'oneTimeCredits';
              const isOpen = canExpand && Boolean(expandedRows[row.key]);
              return (
                <div key={row.key} className='c-overview-balance-row'>
                  <button
                    type='button'
                    className='c-overview-balance-toggle'
                    aria-expanded={canExpand ? isOpen : undefined}
                    style={canExpand ? undefined : { cursor: 'default' }}
                    onClick={() => {
                      if (!canExpand) return;
                      setExpandedRows((prev) => ({
                        ...prev,
                        [row.key]: !prev[row.key],
                      }));
                    }}
                  >
                    <div className='c-overview-balance-header'>
                      <span className='c-overview-balance-label'>
                        {canExpand && (
                          <span
                            className='c-overview-balance-chevron'
                            data-open={isOpen || undefined}
                          >
                            ▾
                          </span>
                        )}
                        {row.icon}
                        {row.label}
                      </span>
                      <span className='c-overview-balance-rate'>
                        {row.rate}
                      </span>
                    </div>
                    <div className='c-usage-bar-wrap'>
                      <span className='c-usage-bar-pct'>
                        {Math.max(0, row.total - row.used).toLocaleString()}{' '}
                        {row.key === 'database' ? 'records' : 'credits'}{' '}
                        remaining
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className='c-overview-balance-detail'>
                      {row.detailTitle && (
                        <div className='c-overview-balance-detail-title'>
                          {row.detailTitle}
                        </div>
                      )}
                      {row.detail.length === 0 ? (
                        <div className='c-overview-balance-detail-empty'>
                          {row.detailEmpty}
                        </div>
                      ) : (
                        row.detail.map((d) => (
                          <div
                            key={(d as any).key ?? d.label}
                            className='c-overview-balance-detail-row'
                          >
                            <span>{d.label}</span>
                            <strong>
                              {d.value.toLocaleString()}{' '}
                              <span className='c-overview-balance-detail-unit'>
                                {d.unit}
                              </span>
                            </strong>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Usage card */}
        <div className='c-overview-card'>
          <div className='c-overview-card-header'>
            <div className='c-usage-icon-wrap'>
              <IconDatabase />
            </div>
            <div className='c-usage-info'>
              <h3 className='c-usage-title'>Usage</h3>
              <p className='c-usage-cycle'>Last {range.label.toLowerCase()}</p>
            </div>
            <select
              className='ms-credit-select c-overview-range-select'
              aria-label='Usage time range'
              value={rangeKey}
              onChange={(e) => setRangeKey(e.target.value as UsageRangeKey)}
            >
              {USAGE_RANGES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className='c-overview-usage-total'>
            <span className='c-overview-usage-total-value'>
              {totalSpend.toLocaleString()}
            </span>
            <span className='c-overview-usage-total-label'>
              credits spent in the last {range.label.toLowerCase()}
            </span>
          </div>
          {totalGrants > 0 && (
            <div className='c-overview-usage-total c-overview-usage-total-added'>
              <span className='c-overview-usage-total-value'>
                +{totalGrants.toLocaleString()}
              </span>
              <span className='c-overview-usage-total-label'>
                credits added in the last {range.label.toLowerCase()}
              </span>
            </div>
          )}
          <div className='c-overview-linechart'>
            <Line data={chartData} options={chartOptions} />
          </div>
          <button
            type='button'
            className='pricing-btn pricing-btn-outline c-overview-details-btn'
            onClick={() => setDetailsOpen(true)}
          >
            More usage details
          </button>
          {detailsOpen && (
            <div
              className='pricing-overlay'
              onClick={() => setDetailsOpen(false)}
            >
              <div
                className='c-overview-usage-modal'
                onClick={(e) => e.stopPropagation()}
              >
                <div className='c-overview-usage-modal-header'>
                  <h3 className='c-usage-title'>Usage details</h3>
                  <button
                    type='button'
                    className='icon-btn-round'
                    onClick={() => setDetailsOpen(false)}
                  >
                    <IconX />
                  </button>
                </div>
                <div className='c-overview-usage-details'>
                  <div className='c-overview-usage-detail-summary'>
                    <span>
                      Credits added:{' '}
                      <strong className='c-overview-usage-detail-added'>
                        +{totalGrants.toLocaleString()}
                      </strong>
                    </span>
                    <span>
                      Credits spent:{' '}
                      <strong className='c-overview-usage-detail-spent'>
                        -{totalSpend.toLocaleString()}
                      </strong>
                    </span>
                    <span>
                      Total entries:{' '}
                      <strong>{allTransactionsSorted.length.toLocaleString()}</strong>
                    </span>
                  </div>
                  {allTransactionsSorted.length === 0 ? (
                    <div className='c-usage-admin-empty'>
                      No credit activity in the last {range.label.toLowerCase()}
                      .
                    </div>
                  ) : (
                    <table className='c-usage-admin-tx-table'>
                      <thead>
                        <tr>
                          <th>Credit Type</th>
                          <th>Source</th>
                          <th>Workflow Step</th>
                          <th>Date</th>
                          <th>User Name</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allTransactionsSorted.map((tx, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={`${tx.created_at}-${idx}`}>
                            <td>{creditTypeLabel(tx)}</td>
                            <td>{SOURCE_LABELS[tx.source] ?? tx.source}</td>
                            <td title={tx.run_id ? `Run ${tx.run_id}` : undefined}>
                              {tx.workflow_step || '—'}
                            </td>
                            <td>
                              {moment(tx.created_at).format(
                                'MM/DD/YYYY h:mm A',
                              )}
                            </td>
                            <td>{tx.user_name || tx.user_email || '—'}</td>
                            <td
                              className={`num ${
                                tx.credit_delta >= 0
                                  ? 'c-overview-usage-detail-added'
                                  : 'c-overview-usage-detail-spent'
                              }`}
                            >
                              {tx.credit_delta >= 0 ? '+' : '-'}
                              {Math.abs(
                                Number(tx.credit_delta),
                              ).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='c-usage-unlimited-note c-overview-note'>
          <IconShieldCheck />
          <span>
            Core tools (tariff simulation, HTS codes, screening) remain{' '}
            <strong>unlimited</strong> — credits apply only to AI-powered
            features.
          </span>
        </div>
      </div>
      {howOpen && (
        <div className='pricing-overlay' onClick={() => setHowOpen(false)}>
          <div className='msp-how-modal' onClick={(e) => e.stopPropagation()}>
            <div className='msp-how-title'>How credits work</div>
            <div className='msp-how-row'>
              <div className='msp-how-box' data-accent='analyzers'>
                <div className='msp-how-box-header'>
                  <IconTarget />
                  <div className='msp-how-box-title'>
                    Analyze & Work Credits
                  </div>
                </div>
                <div className='msp-how-box-text'>
                  Analyze & Work credits power the Analyzers and Workflows. Bulk
                  Tariff Analyzer: 1 credit per 5 HTS codes, rounded up; ACE
                  Analyzer: 1 credit per entry (unique Entry Summary Number);
                  AES Analyzer: 1 credit per shipment (unique Shipment Reference
                  Number). Workflows: Import = 4, Export = 10, Design Assist =
                  10, Beneficial Ownership = 10 credits per completed run.
                  Agentic Workflows only charge credits when a run finishes
                  successfully. A one-time pack is available anytime from the
                  subscriptions page and credits never expire.
                </div>
              </div>
              <div className='msp-how-box'>
                <div className='msp-how-box-header'>
                  <IconDatabase />
                  <div className='msp-how-box-title'>Database Records</div>
                </div>
                <div className='msp-how-box-text'>
                  Database records represent your storage capacity — the total
                  number of rows you can process and store for all of your
                  databases in BITE DB for either Products or Entities. Unlike
                  one-time credits, database records are a monthly recurring
                  allocation that do not decrement with usage; they define the
                  maximum size of your stored data. Higher-tier plans include
                  larger database allocations to support growing teams and large
                  dataset uploads.
                </div>
              </div>
            </div>
            <div className='msp-how-footer'>
              <IconCreditCard />
              <span>
                Additional one-time credit packs are available from the
                subscriptions page.
              </span>
            </div>
            <div className='msp-how-close-wrap'>
              <button
                className='pricing-btn pricing-btn-outline'
                onClick={() => setHowOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreditsUsageOverview;
